import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      /* ⭐ THE PROSE IS IN NORMAL INK; THE ROLE IS IN THE RAIL AND THE GLYPH.
         The old variants set the whole body text in a raw amber or emerald ink
         (named here in prose, not quoted: Tailwind's scanner reads comments
         and would generate a live rule for a class nobody uses), which is
         the text equivalent of tinting a panel —
         a paragraph of brown reads as damage rather than as information, and
         it was the second source of the "burgundy text" complaint on PetsBOXX.
         Colour carries the ROLE (a 4px left rail, the icon, the tint); the
         sentence stays in card ink so it can simply be read. */
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "bg-destructive-100 text-card-foreground border-destructive-200 border-l-4 border-l-destructive [&>svg]:text-destructive",
        success:
          "bg-success-100 text-card-foreground border-success-200 border-l-4 border-l-success [&>svg]:text-success",
        warning:
          "bg-warning-100 text-card-foreground border-warning-200 border-l-4 border-l-warning [&>svg]:text-warning",
        info:
          "bg-info-100 text-card-foreground border-info-200 border-l-4 border-l-info [&>svg]:text-info",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
))
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
